import { Injectable, inject } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, of } from 'rxjs';
import { AuthService } from './auth.service';
import { EntityParticipantsService } from './entity-participants.service';
import { fetchAllPages } from '../utils/paged-fetch';
import { PLATFORM_CHAT_ENTITY_TYPE } from '../components/platform-comments-chat/platform-chat-entity.const';

@Injectable({
  providedIn: 'root',
})
export class ProjectUserService {
  private readonly entityParticipants: EntityParticipantsService = inject(EntityParticipantsService);

  constructor(
    private readonly api: ApiService,
    private readonly authService: AuthService
  ) {}

  public mthdReInvite(projectId: string, username: string): Observable<any> {
    return this.api.httpPost('/api/projects/' + projectId + '/users/reinvite', [username]);
  }

  /**
   * No-op: POST /api/projects/{projectId}/users/remove is retired (RBAC permission migration).
   */
  public deleteUsers(_projectId: string, _users: any[]): Observable<any> {
    return of(null);
  }

  /**
   * Project users via the unified per-entity participants endpoint (same as the task
   * surfaces); the legacy /api/users/project/{id} listing is retired. Rows carry
   * username/name/email/avatar only — no userPermissionType or role data.
   */
  public getProjectUsers(id: string): Observable<any> {
    // Pre-migration parity: a 403 on the project listing shows the no-access overlay and
    // redirects instead of silently rendering an empty roster.
    return this.entityParticipants.getParticipants(PLATFORM_CHAT_ENTITY_TYPE.PROJECT, id, false, {
      redirectOnForbidden: true,
    });
  }

  public getProjectParticipantWithUserActivationStatus(projectId: string): Observable<any> {
    return this.api.httpGet('/api/projects/' + projectId + '/getProjectParticipantsWithActivationStatus');
  }

  public acceptInvitation(projectId: string): Observable<any> {
    return this.api.httpPut(`/api/projects/${projectId}/user/accept`, null);
  }

  public rejectInvitation(projectId: string): Observable<any> {
    return this.api.httpPut(`/api/projects/${projectId}/user/reject`, null);
  }

  public blockUser(projectId: string, userName: string): Observable<any> {
    return this.api.httpPut(`/api/projects/${projectId}/users/${userName}/block`, {});
  }

  public unblockUser(projectId: string, userName: string): Observable<any> {
    return this.api.httpPut(`/api/projects/${projectId}/users/${userName}/unblock`, {});
  }

  public getProjectParticipants(): Observable<any> {
    return this.api.httpGet('/api/projects/' + this.authService.getAuthUserName() + '/users');
  }

  public getProjectsUser(userName: any): Observable<any> {
    return fetchAllPages((page: number) => this.api.httpGet(`/api/projects/users/${userName}?page=${page}`));
  }
}
